import {Collapse} from '@mui/material';
import {
  ConfigObjectType,
  enumContainer,
  ExpandDownButton,
  IconHeaderWithSearch,
  LightIconButton,
  LightLayoutNodeFCProps,
  MaximizeButton,
  omitNullsy,
  rawRenderingLib,
  utils,
} from '@wallaby-ui/core';
import {
  FILTER_ACTIVE_ICON_COLORS,
  getRiskFactorMedicationLabel,
  LightDateRangeFilter,
  LightMedicationAtcFilter2,
  LightSavedFiltersButton,
  LightSelectedFilters,
  LightSelectFilter,
  LightTextFilter,
  MerieSchema,
  ShortcutEnum,
  sortRiskFactorsData,
  TableSorting,
  useFormatters,
} from '@wallaby-ui/document';
import {isEqual} from 'lodash';
import React from 'react';
import {useIntl} from 'react-intl';
import {useStore} from 'react-redux';

import {HospitalizationState} from './HospitalizationDetailLayoutV2';
import {HospitalizationDiagnosticsTimeline, I18N, TimelineRowType} from './HospitalizationDiagnosticsTimeline';
import {useAppSelector} from 'src/js/store_hooks';

export type HospitalizationTimelineFilters = {
  startDate?: string;
  endDate?: string;
  floatingShortcutName?: ShortcutEnum | null;
  eventCategories?: string[];
  eventTypes?: string[];
  annotationText?: string;
  documentName?: string;
  riskFactors?: string[];
  atcHierarchyIds?: string[];
};
export const DEFAULT_HOSPITALIZATION_TIMELINE_FILTERS: HospitalizationTimelineFilters = {};
export const DEFAULT_HOSPITALIZATION_TIMELINE_SORTING: TableSorting = {
  field: 'date',
  direction: 'desc',
};
export type HospitalizationTimelineProps = {
  state: HospitalizationState;
  changeState: (diff: Partial<HospitalizationState>) => void;
  storageIdPrefix: string;
  config: ConfigObjectType;
  riskFactors: MerieSchema.components['schemas']['RiskFactorDto'][];
  timelineDefaultScroll: number;
  setTimelineDefaultScroll: (v: number) => void;
  sortedTimelineEvents: TimelineRowType[];
  timelineIsLoading: boolean;
  encounterId: number | undefined;
  pharmaRecommendations: MerieSchema.components['schemas']['PharmaRecommendationsDto'] | undefined;
};
export const HospitalizationDetailTimelineV2: React.FC<LightLayoutNodeFCProps & HospitalizationTimelineProps> = (props) => {
  const {
    state,
    changeState,
    storageIdPrefix,
    config,
    riskFactors,
    timelineDefaultScroll,
    setTimelineDefaultScroll,
    sortedTimelineEvents,
    timelineIsLoading,
    encounterId,
    minimized,
    expanded,
    maximized,
    setMinimizedMaximized,
    pharmaRecommendations,
  } = props;
  const timelineOnScroll = (event: Event) => setTimelineDefaultScroll((event.target as HTMLElement).scrollTop);
  // data
  const enumMap = useAppSelector((state) => state[enumContainer.ENUM_REDUCER_STATE_ID].enumMap);
  const intl = useIntl();
  const store = useStore();
  const {tEnum} = useFormatters();
  const riskFactorOptions = sortRiskFactorsData(riskFactors, new Set())
    .filter((v) => v.createdBy === 'MERIE')
    .map((v) => {
      const riskFactorType = rawRenderingLib.translatedEnumValueRenderer(
        {
          enumName: 'RiskFactorTypeEnum',
          enumValueName: v.type,
        },
        {intl, store}
      );
      const riskFactorLabel = getRiskFactorMedicationLabel(v, tEnum);
      return {
        id: String(v.id),
        label: riskFactorType + (riskFactorLabel ? ` - ${riskFactorLabel}` : ''),
      };
    });
  const isBigMode = !minimized || maximized;
  const hasActiveTimelineFilter = !isEqual(omitNullsy(state.timeline), omitNullsy(DEFAULT_HOSPITALIZATION_TIMELINE_FILTERS));
  return (
    <div className='dl-light-box'>
      <IconHeaderWithSearch iconName='timelineIcon' label='hospitalization.detail.timeline'>
        <div className='dl-header-buttons-row' style={{gap: 6}}>
          <LightSelectedFilters
            width='auto'
            hasFilters={hasActiveTimelineFilter}
            handleResetFilters={() => {
              changeState({
                timeline: DEFAULT_HOSPITALIZATION_TIMELINE_FILTERS,
              });
              setTimelineDefaultScroll(0);
            }}
            hasSorting={!isEqual(omitNullsy(state.timelineSorting), omitNullsy(DEFAULT_HOSPITALIZATION_TIMELINE_SORTING))}
            handleResetSorting={() => {
              changeState({
                timelineSorting: DEFAULT_HOSPITALIZATION_TIMELINE_SORTING,
              });
              setTimelineDefaultScroll(0);
            }}
          />
          <div className='dl-header-icons-row' style={{gap: 6}}>
            <LightIconButton
              iconName='filterIcon'
              useFixedViewBox='0 0 20 16'
              style={{width: 20, height: 16}}
              title={state.areFiltersOpen.timeline ? 'layout.filters.closeFilters' : 'layout.filters.openFilters'}
              onClick={() => {
                if (minimized && !state.areFiltersOpen.timeline) setMinimizedMaximized({minimized: false});
                changeState({areFiltersOpen: {...state.areFiltersOpen, timeline: !state.areFiltersOpen.timeline}});
              }}
              selected={state.areFiltersOpen.timeline}
              colors={hasActiveTimelineFilter && !state.areFiltersOpen.timeline ? FILTER_ACTIVE_ICON_COLORS : undefined}
            />
            <LightSavedFiltersButton
              storageIdPrefix={`${storageIdPrefix}.timeline`}
              component='hospitalizationTimeline'
              config={config}
              filter={state.timeline}
              setFilter={(newFilter) => changeState({timeline: newFilter as HospitalizationTimelineFilters})}
            />
            <ExpandDownButton expanded={expanded} toggleExpanded={() => setMinimizedMaximized({expanded: !expanded, maximized: false})} />
            <MaximizeButton maximized={maximized} toggleMaximized={() => setMinimizedMaximized({maximized: !maximized})} />
          </div>
        </div>
      </IconHeaderWithSearch>
      {isBigMode && (
        <>
          <Collapse style={{width: '100%', minHeight: 'none'}} in={state.areFiltersOpen.timeline} timeout={500}>
            <div className='dl-filters-row'>
              <LightDateRangeFilter
                config={{
                  name: 'dateRange',
                  ui: {
                    label: 'hospitalization.detail.timeline.dateRange',
                  },
                }}
                value={
                  state.timeline.startDate && state.timeline.endDate
                    ? {
                        startDate: state.timeline.startDate,
                        endDate: state.timeline.endDate,
                        shortcut: state.timeline.floatingShortcutName,
                      }
                    : null
                }
                onChange={(newValue) => {
                  changeState({
                    timeline: {
                      ...state.timeline,
                      startDate: newValue?.startDate,
                      endDate: newValue?.endDate,
                      floatingShortcutName: newValue?.shortcut,
                    },
                  });
                  setTimelineDefaultScroll(0);
                }}
                dateOnly
                showShortcuts
              />
              <LightSelectFilter
                config={{
                  name: 'eventCategories',
                  ui: {label: 'hospitalization.detail.timeline.eventCategories'},
                }}
                options={enumMap.MerieEventCategoryTypeEnum}
                value={state.timeline.eventCategories}
                onChange={(newValue) => {
                  changeState({timeline: {...state.timeline, eventCategories: newValue}});
                  setTimelineDefaultScroll(0);
                }}
                multi
              />
              <LightSelectFilter
                config={{
                  name: 'eventTypes',
                  ui: {
                    label: 'hospitalization.detail.timeline.eventType',
                  },
                }}
                options={enumMap.MerieEventTypeEnum}
                value={state.timeline.eventTypes}
                onChange={(newValue) => {
                  changeState({
                    timeline: {
                      ...state.timeline,
                      eventTypes: newValue,
                    },
                  });
                  setTimelineDefaultScroll(0);
                }}
                multi
              />
              <LightTextFilter
                config={{
                  name: 'annotationText',
                  ui: {label: utils.TranslateUtils.translate(I18N.ANNOTATION, intl)},
                }}
                value={state.timeline.annotationText ?? null}
                onChange={(newValue) => {
                  changeState({timeline: {...state.timeline, annotationText: newValue ?? undefined}});
                  setTimelineDefaultScroll(0);
                }}
              />
              <LightTextFilter
                config={{
                  name: 'documentName',
                  ui: {label: utils.TranslateUtils.translate(I18N.DOCUMENT_NAME, intl)},
                }}
                value={state.timeline.documentName ?? null}
                onChange={(newValue) => {
                  changeState({timeline: {...state.timeline, documentName: newValue ?? undefined}});
                  setTimelineDefaultScroll(0);
                }}
              />
              <LightSelectFilter
                config={{
                  name: 'riskFactors',
                  ui: {
                    label: 'hospitalization.detail.timeline.riskFactors',
                  },
                }}
                options={riskFactorOptions}
                value={state.timeline.riskFactors}
                onChange={(newValue) => {
                  changeState({
                    timeline: {
                      ...state.timeline,
                      riskFactors: newValue,
                    },
                  });
                  setTimelineDefaultScroll(0);
                }}
                multi
              />
              <LightMedicationAtcFilter2
                appConfig={config}
                config={{
                  name: 'atcHierarchyIds',
                  ui: {
                    label: 'hospitalization.detail.timeline.medications',
                  },
                }}
                multi
                value={state.timeline.atcHierarchyIds ?? null}
                onChange={(newValue) => {
                  changeState({
                    timeline: {
                      ...state.timeline,
                      atcHierarchyIds: newValue ?? undefined,
                    },
                  });
                  setTimelineDefaultScroll(0);
                }}
              />
            </div>
          </Collapse>
          <HospitalizationDiagnosticsTimeline
            style={{flex: 1}}
            config={config}
            defaultScroll={timelineDefaultScroll}
            onScroll={timelineOnScroll}
            filters={state}
            changeFilters={changeState}
            storageIdPrefix={`${storageIdPrefix}.timeline`}
            versionSuffix='/v2'
            encounterId={encounterId}
            sortedTimelineEvents={sortedTimelineEvents}
            timelineEventAnnotationFilter={state.timeline.annotationText}
            timelineIsLoading={timelineIsLoading}
            pharmaRecommendations={pharmaRecommendations}
            maximized={maximized}
            setMinimizedMaximized={setMinimizedMaximized}
          />
        </>
      )}
    </div>
  );
};
HospitalizationDetailTimelineV2.displayName = 'HospitalizationDetailTimelineV2';
